import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import AIActivityIndicator from './components/AIActivityIndicator';
import Dashboard from './pages/Dashboard';
import TasksPage from './pages/TasksPage';
import StrategyPage from './pages/StrategyPage';
import ContentPage from './pages/ContentPage';
import CalendarPage from './pages/CalendarPage';
import SchedulePage from './pages/SchedulePage';
import AssistantChatPage from './pages/AssistantChatPage';
import SettingsPage from './pages/SettingsPage';
import SupervisorPage from './pages/SupervisorPage';
import EngagementPage from './pages/EngagementPage';
import MediaPage from './pages/MediaPage';
import LoginPage from './pages/LoginPage';
import OnboardingCollectorPage from './pages/OnboardingCollectorPage';
import ActivationPage from './pages/ActivationPage';
import PaymentPendingPage from './pages/PaymentPendingPage';
import BetaNotice from './components/BetaNotice';
import { Spinner, ToastProvider } from './components/ui';
import { getActivationStatus, getCurrentUser, getWorkspaces } from './api';
import type { PageKey, User, Workspace } from './types';
import { useTheme } from './hooks/useTheme';
import { useActiveJobs } from './hooks/useActiveJobs';
import { useHumanTaskCount } from './hooks/useHumanTaskCount';
import { APP_VERSION } from './version';
import { identifyUser, resetAnalytics } from './analytics';
import './App.css';

const PAGE_PATHS: Record<PageKey, string> = {
  dashboard: '/',
  tasks: '/tasks',
  strategy: '/strategy',
  // Pillars and campaigns now live on the Strategy page (old links redirect).
  pillars: '/strategy',
  plans: '/strategy',
  content: '/content',
  calendar: '/calendar',
  schedule: '/schedule',
  assistant: '/assistant',
  media: '/media',
  supervisor: '/agents/supervisor',
  engagement: '/agents/engagement',
  settings: '/settings',
  activate: '/activate',
};

const APP_NAME = 'Conistra';

const PAGE_TITLES: Record<PageKey, string> = {
  dashboard: 'Dashboard',
  tasks: 'Needs You',
  strategy: 'Strategy',
  pillars: 'Strategy',
  plans: 'Strategy',
  content: 'Content',
  calendar: 'Calendar',
  schedule: 'Schedule',
  assistant: 'Assistant',
  media: 'Media',
  supervisor: 'Supervisor Agent',
  engagement: 'Engagement Agent',
  settings: 'Settings',
  activate: 'Activate',
};

function buildDocumentTitle(screenTitle: string): string {
  return `${screenTitle} | ${APP_NAME}`;
}

function screenTitleFromPath(pathname: string): string {
  const page = pathToPage(pathname);
  return PAGE_TITLES[page] ?? 'Dashboard';
}

function pathToPage(pathname: string): PageKey {
  // Exact match first
  const entry = Object.entries(PAGE_PATHS).find(([, path]) => path === pathname);
  if (entry) return entry[0] as PageKey;
  // Handle sub-routes like /content/:outlineId
  const parent = Object.entries(PAGE_PATHS).find(
    ([, path]) => path !== '/' && pathname.startsWith(path + '/')
  );
  return (parent?.[0] as PageKey) ?? 'dashboard';
}

function AppInner({
  user,
  workspace,
  onLogout,
  onWorkspaceUpdate,
  onUserUpdate,
}: {
  user: User;
  workspace: Workspace;
  onLogout: () => void;
  onWorkspaceUpdate: (_w: Workspace) => void;
  onUserUpdate: (_u: User) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activationCompleted, setActivationCompleted] = useState(false);

  // Apply workspace brand colors as CSS custom properties
  useTheme(workspace.theme_colors);

  // Poll for active AI jobs to show the global activity indicator
  // Exclude ONBOARDING jobs — the ActivationPage has its own progress UI.
  const { activeJobs: allActiveJobs } = useActiveJobs(workspace.id);
  const activeJobs = allActiveJobs.filter((j) => j.action !== 'ONBOARDING');

  // Open human tasks ("Needs You" inbox) — shown as a badge on the sidebar.
  const { openCount: tasksOpenCount, refresh: refreshTaskCount } = useHumanTaskCount(workspace.id);

  const currentPage = pathToPage(location.pathname);
  const isActivationRoute = location.pathname === '/activate';

  // Mobile nav drawer: lock the page behind it and let Escape dismiss it.
  // Without the lock, dragging on the drawer scrolls the page underneath.
  useEffect(() => {
    if (!sidebarOpen) return;

    document.body.classList.add('body--locked');
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove('body--locked');
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [sidebarOpen]);

  useEffect(() => {
    let mounted = true;
    getActivationStatus(workspace.id)
      .then((status) => {
        if (!mounted) return;
        setActivationCompleted(Boolean(status.activation_completed));
      })
      .catch(() => {
        if (!mounted) return;
        setActivationCompleted(false);
      });

    return () => {
      mounted = false;
    };
  }, [workspace.id]);

  const handleNavigate = (page: PageKey) => {
    navigate(PAGE_PATHS[page]);
  };

  return (
    <div className="app">
      <Sidebar
        currentPage={currentPage}
        onNavigate={handleNavigate}
        user={user}
        workspace={workspace}
        isNavigationLocked={isActivationRoute}
        onLogout={onLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        tasksOpenCount={tasksOpenCount}
      />
      <div className="app-body">
        <TopBar
          currentPage={currentPage}
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          workspace={workspace}
          onNavigate={handleNavigate}
        />
        <main className="main-content">
          <div className="page-enter">
            <Routes>
              <Route
                path="/"
                element={
                  <Dashboard
                    workspaceId={workspace.id}
                    workspace={workspace}
                    onNavigate={handleNavigate}
                  />
                }
              />
              <Route path="/assistant" element={<AssistantChatPage workspaceId={workspace.id} />} />
              <Route
                path="/tasks"
                element={
                  <TasksPage
                    workspaceId={workspace.id}
                    onNavigate={handleNavigate}
                    onTasksChanged={refreshTaskCount}
                  />
                }
              />
              <Route
                path="/strategy"
                element={<StrategyPage workspaceId={workspace.id} onNavigate={handleNavigate} />}
              />
              <Route path="/pillars" element={<Navigate to="/strategy" replace />} />
              <Route path="/plans" element={<Navigate to="/strategy" replace />} />
              <Route
                path="/content"
                element={
                  <ContentPage
                    workspaceId={workspace.id}
                    workspace={workspace}
                    onNavigate={handleNavigate}
                    activeJobs={activeJobs}
                  />
                }
              />
              <Route
                path="/content/:outlineId"
                element={
                  <ContentPage
                    workspaceId={workspace.id}
                    workspace={workspace}
                    onNavigate={handleNavigate}
                    activeJobs={activeJobs}
                  />
                }
              />
              <Route
                path="/calendar"
                element={
                  <CalendarPage
                    workspaceId={workspace.id}
                    autoApprove={workspace.auto_approve}
                    onNavigate={handleNavigate}
                  />
                }
              />
              <Route
                path="/schedule"
                element={<SchedulePage workspaceId={workspace.id} onNavigate={handleNavigate} />}
              />
              <Route
                path="/media"
                element={<MediaPage workspaceId={workspace.id} />}
              />
              <Route
                path="/agents/supervisor"
                element={
                  <SupervisorPage
                    workspace={workspace}
                    onWorkspaceUpdate={onWorkspaceUpdate}
                    onNavigate={handleNavigate}
                  />
                }
              />
              <Route
                path="/agents/engagement"
                element={
                  <EngagementPage
                    workspace={workspace}
                    onWorkspaceUpdate={onWorkspaceUpdate}
                    onNavigate={handleNavigate}
                  />
                }
              />
              <Route
                path="/settings"
                element={
                  <SettingsPage
                    workspace={workspace}
                    onWorkspaceUpdate={onWorkspaceUpdate}
                    user={user}
                    onUserUpdate={onUserUpdate}
                  />
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
              <Route
                path="/activate"
                element={
                  activationCompleted ? (
                    <Navigate to="/" replace />
                  ) : (
                    <ActivationPage
                      workspaceId={workspace.id}
                      onNavigate={handleNavigate}
                      onWorkspaceUpdate={onWorkspaceUpdate}
                    />
                  )
                }
              />
            </Routes>
          </div>
        </main>
      </div>
      <AIActivityIndicator jobs={activeJobs} workspaceId={workspace.id} />
    </div>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const withBetaNotice = (content: React.ReactNode) => (
    <>
      {content}
      {APP_VERSION ? <div className="app-version-pill">v{APP_VERSION}</div> : null}
      <BetaNotice />
    </>
  );

  // Derive initial auth mode from the URL path (e.g. from marketing site CTAs)
  const getInitialAuthMode = (): 'login' | 'onboarding' => {
    if (location.pathname === '/signup') return 'onboarding';
    return 'login';
  };

  const getInitialPlanCode = (): string => {
    if (location.pathname === '/signup') {
      const params = new URLSearchParams(location.search);
      return params.get('plan') || 'creator';
    }
    return 'creator';
  };

  const [user, setUser] = useState<User | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [authMode, setAuthMode] = useState<'login' | 'onboarding'>(getInitialAuthMode);
  const [onboardingPlanCode, setOnboardingPlanCode] = useState<string>(getInitialPlanCode);

  useEffect(() => {
    let screenTitle = 'Conistra';

    if (loading) {
      screenTitle = 'Loading';
    } else if (!user || !workspace) {
      if (location.pathname === '/signup' || authMode === 'onboarding') {
        screenTitle = 'Get Started';
      } else {
        screenTitle = 'Login';
      }
    } else {
      screenTitle = screenTitleFromPath(location.pathname);
    }

    document.title = buildDocumentTitle(screenTitle);
  }, [loading, user, workspace, location.pathname, authMode]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData.user);
        // Returning session — re-assert the identity on every boot.
        identifyUser(userData.user);

        const workspacesData = await getWorkspaces();
        const firstWorkspace = workspacesData.results?.[0] || null;
        setWorkspace(firstWorkspace);
      } catch (err) {
        console.log('Not authenticated');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogin = async (userData: User, workspaceData: Workspace | null) => {
    setUser(userData);
    setWorkspace(workspaceData);
    // Clear /signup or /login from the URL after successful login
    if (location.pathname === '/signup' || location.pathname === '/login') {
      navigate('/', { replace: true });
    }
  };

  const handleLogout = () => {
    setUser(null);
    setWorkspace(null);
    setAuthMode('login');
    // Drop the identity so the next visitor on this browser isn't merged in.
    resetAnalytics();
    navigate('/', { replace: true });
  };

  if (loading) {
    return withBetaNotice(
      <div className="app-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user || !workspace) {
    if (authMode === 'onboarding') {
      return withBetaNotice(
        <ToastProvider>
          <OnboardingCollectorPage
            planCode={onboardingPlanCode}
            onComplete={handleLogin}
            onBack={() => setAuthMode('login')}
          />
        </ToastProvider>
      );
    }
    return withBetaNotice(
      <ToastProvider>
        <LoginPage
          onLogin={handleLogin}
          onSignup={(planCode) => {
            setOnboardingPlanCode(planCode ?? 'creator');
            setAuthMode('onboarding');
          }}
        />
      </ToastProvider>
    );
  }

  const isPaymentPending = workspace.is_pending || workspace.plan_status === 'pending';
  if (isPaymentPending) {
    return withBetaNotice(
      <ToastProvider>
        <PaymentPendingPage
          workspace={workspace}
          onLogout={handleLogout}
          onPaymentConfirmed={(updatedWorkspace) => {
            setWorkspace(updatedWorkspace);
            navigate('/activate', { replace: true });
          }}
        />
      </ToastProvider>
    );
  }

  return withBetaNotice(
    <ToastProvider>
      <AppInner
        user={user}
        workspace={workspace}
        onLogout={handleLogout}
        onWorkspaceUpdate={(updated) => setWorkspace(updated)}
        onUserUpdate={(updated) => setUser(updated)}
      />
    </ToastProvider>
  );
}

export default App;
